import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface TagOption {
  id: number
  name: string
}

interface Props {
  [key: string]: unknown
  tags: TagOption[]
}

export default function BookNew() {
  const { tags } = usePage<Props>().props

  const { data, setData, post, processing, errors, transform } = useForm<{
    title: string
    author: string
    book_type: string
    description: string
    isbn: string
    publisher: string
    year_published: string
    tag_list: string
    pdf: File | null
  }>({
    title: "",
    author: "",
    book_type: "physical",
    description: "",
    isbn: "",
    publisher: "",
    year_published: "",
    tag_list: "",
    pdf: null,
  })

  transform(
    (data) =>
      ({
        book: {
          ...data,
          pdf: data.pdf || undefined,
        },
      }) as any,
  )

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/books", {
      forceFormData: !!data.pdf,
    })
  }

  function addTag(tagName: string) {
    const current = data.tag_list
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
    if (!current.includes(tagName)) {
      setData("tag_list", [...current, tagName].join(", "))
    }
  }

  function removeTag(tagName: string) {
    const current = data.tag_list
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
    setData("tag_list", current.filter((t) => t !== tagName).join(", "))
  }

  const currentTags = data.tag_list
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)

  return (
    <>
      <Head title="Add Book - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/books">Library</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>New Book</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/books">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Add New Book</h1>
          </div>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader>
                <CardTitle>Book Details</CardTitle>
              </CardHeader>
              <CardContent>
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2 space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        value={data.title}
                        onChange={(e) => setData("title", e.target.value)}
                        placeholder="e.g., The Pragmatic Programmer"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="author">Author</Label>
                      <Input
                        id="author"
                        value={data.author}
                        onChange={(e) => setData("author", e.target.value)}
                        placeholder="e.g., Andrew Hunt, David Thomas"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={data.book_type} onValueChange={(v) => setData("book_type", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="physical">Physical</SelectItem>
                          <SelectItem value="digital">Digital</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="isbn">ISBN</Label>
                      <Input
                        id="isbn"
                        value={data.isbn}
                        onChange={(e) => setData("isbn", e.target.value)}
                        placeholder="Optional"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="publisher">Publisher</Label>
                      <Input
                        id="publisher"
                        value={data.publisher}
                        onChange={(e) => setData("publisher", e.target.value)}
                        placeholder="Optional"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="year_published">Year Published</Label>
                      <Input
                        id="year_published"
                        type="number"
                        min="1800"
                        max={new Date().getFullYear() + 1}
                        value={data.year_published}
                        onChange={(e) => setData("year_published", e.target.value)}
                        placeholder="e.g., 2024"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      rows={4}
                      value={data.description}
                      onChange={(e) => setData("description", e.target.value)}
                      placeholder="Brief description of the book..."
                    />
                  </div>

                  <Separator />

                  {/* Tags */}
                  <div className="space-y-2">
                    <Label htmlFor="tag_list">Tags</Label>
                    <Input
                      id="tag_list"
                      value={data.tag_list}
                      onChange={(e) => setData("tag_list", e.target.value)}
                      placeholder="Comma-separated tags, e.g., programming, design"
                    />
                    {currentTags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {currentTags.map((tag) => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="cursor-pointer"
                            onClick={() => removeTag(tag)}
                          >
                            {tag} &times;
                          </Badge>
                        ))}
                      </div>
                    )}
                    {tags.length > 0 && (
                      <div className="pt-1">
                        <p className="mb-1 text-xs text-muted-foreground">Suggestions:</p>
                        <div className="flex flex-wrap gap-1">
                          {tags
                            .filter((t) => !currentTags.includes(t.name))
                            .slice(0, 15)
                            .map((tag) => (
                              <Badge
                                key={tag.id}
                                variant="outline"
                                className="cursor-pointer hover:bg-secondary/80 text-xs"
                                onClick={() => addTag(tag.name)}
                              >
                                + {tag.name}
                              </Badge>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* PDF Upload (digital only) */}
                  {data.book_type === "digital" && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <Label htmlFor="pdf">PDF File</Label>
                        <Input
                          id="pdf"
                          type="file"
                          accept="application/pdf"
                          onChange={(e) => setData("pdf", e.target.files?.[0] || null)}
                        />
                        <p className="text-xs text-muted-foreground">
                          Upload PDF for digital books (max 50MB). Pages and file size will be extracted automatically.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
              <CardFooter className="justify-end gap-2 border-t">
                <Button variant="ghost" asChild>
                  <a href="/books">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Adding..." : "Add Book"}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </div>
      </div>
    </>
  )
}

BookNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
